import React, { useState } from 'react';
import { IconButton, ListItem, ListItemIcon, Menu, Popover } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getInitials } from 'utilities';
import {
  MenuItemsLabel,
  SecondaryButton,
  SecondaryLink,
  StyledArrowRight,
  StyledAvatar,
  StyledListItemText,
  StyledMenuItem,
} from './styles';
import { IBottomMenuItem, IIconButtonOnClickEvent } from './types';

const ITEM_HEIGHT = 350;

const BottomMenuItem = (props: IBottomMenuItem) => {
  const { item, anchorElement, setAnchorElement } = props;
  const [avatarAnchorEl, setAvatarAnchorEl] = useState<HTMLButtonElement | null>(null);
  const navigate = useNavigate();

  const handleMenuClick = (event: IIconButtonOnClickEvent<HTMLSpanElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorElement(null);
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (item.displayAvatar) {
      setAvatarAnchorEl(event.currentTarget);
    }
  };

  const handleAvatarPopoverClose = () => {
    setAvatarAnchorEl(null);
  };

  const isAvatarPopoverOpen = Boolean(avatarAnchorEl);

  const renderAvatar = () => {
    if (item?.displayAvatar) {
      return (
        <IconButton
          onClick={handleAvatarClick}
          sx={{ padding: 0 }}
          aria-label="user menu">
          <StyledAvatar src={item.imageUrl}>
            {!item.imageUrl && getInitials({ name: item.primaryText })}
          </StyledAvatar>
        </IconButton>
      );
    }

    if (item.imageUrl) {
      return <StyledAvatar src={item.imageUrl} />;
    }

    return <StyledAvatar>{getInitials({ name: item.primaryText })}</StyledAvatar>;
  };

  return (
    <>
      <ListItem sx={{ padding: 0 }}>
        <ListItemIcon sx={{ justifyContent: 'center', margin: '0 auto' }}>
          {renderAvatar()}
        </ListItemIcon>

        {!item.displayAvatar && (
          <StyledListItemText
            primary={item.primaryText}
            secondary={
              item?.isLink ? (
                <SecondaryLink to={item.slug}>{item.secondaryText}</SecondaryLink>
              ) : (
                item?.handleButtonClick && (
                  <SecondaryButton onClick={item.handleButtonClick} type="button">
                    {item.secondaryText}
                  </SecondaryButton>
                )
              )
            }
          />
        )}

        {item.subItems && item.subItems.length > 0 && (
          <IconButton
            aria-controls={item.slug}
            aria-haspopup="true"
            aria-label="more"
            component="span"
            id={`${item.slug}-button`}
            onClick={handleMenuClick}>
            <StyledArrowRight fontSize="small" />
          </IconButton>
        )}
      </ListItem>

      {item.displayAvatar && (
        <Popover
          open={isAvatarPopoverOpen}
          anchorEl={avatarAnchorEl}
          onClose={handleAvatarPopoverClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}>
          <div style={{ padding: '8px 12px', minWidth: '120px', maxWidth: '220px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 700, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.primaryText}
            </div>
            {item.accountSettingsPath && (
              <SecondaryButton
                onClick={() => {
                  navigate(item.accountSettingsPath!);
                  handleAvatarPopoverClose();
                }}
                type="button"
                sx={{ color: 'inherit', width: '100%', justifyContent: 'flex-start' }}>
                Account Settings
              </SecondaryButton>
            )}
            {item.switchAppLabel && item.onSwitchApp && (
              <SecondaryButton
                onClick={() => {
                  item.onSwitchApp!();
                  handleAvatarPopoverClose();
                }}
                type="button"
                sx={{ color: 'inherit', width: '100%', justifyContent: 'flex-start' }}>
                {item.switchAppLabel}
              </SecondaryButton>
            )}
            {item?.handleButtonClick && (
              <SecondaryButton
                onClick={() => {
                  item.handleButtonClick?.();
                  handleAvatarPopoverClose();
                }}
                type="button"
                sx={{ color: 'inherit', width: '100%', justifyContent: 'flex-start' }}>
                {item.secondaryText}
              </SecondaryButton>
            )}
          </div>
        </Popover>
      )}

      {/* Submenu */}
      {item.subItems && item.subItems.length > 0 && (
        <Menu
          id={`sidebar-bottom-dropdown-${item.slug}`}
          anchorEl={anchorElement}
          open={anchorElement ? anchorElement.getAttribute('aria-controls') === item.slug : false}
          onClose={handleMenuClose}
          slotProps={{
            paper: {
              style: {
                maxHeight: ITEM_HEIGHT,
              },
              square: true,
            },
          }}>
          {item.subItemsLabel && <MenuItemsLabel>{item.subItemsLabel}</MenuItemsLabel>}

          {item.subItems.map(option => (
            <div key={`subitem-${option.slug}`}>
              <StyledMenuItem
                key={option.slug}
                onClick={() => {
                  option.handleItemClick();
                  handleMenuClose();
                  // TODO: Close sidebar
                }}>
                {option?.icon && option.icon} {option.title}
              </StyledMenuItem>
            </div>
          ))}
        </Menu>
      )}
    </>
  );
};

export default BottomMenuItem;
